import { useId, type FormEvent } from "react";
import type { Vessel, VesselInput } from "@/api/vessels";
import { RequestError } from "@/components/RequestError";
import { TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { useFormValues, useSubmitState } from "@/lib/use-form";

type VesselFormProps = {
  initial?: Vessel | null;
  submitLabel?: string;
  onSubmit: (input: VesselInput) => Promise<unknown>;
  onCancel?: () => void;
  onReload?: () => void;
};

export function VesselForm({
  initial,
  submitLabel = "Save vessel",
  onSubmit,
  onCancel,
  onReload,
}: VesselFormProps) {
  const id = useId();
  const { values, set } = useFormValues<VesselInput>({
    name: initial?.name ?? "",
    imo: initial?.imo ?? "",
  });
  const { error, errors, saving, run } = useSubmitState();

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() => onSubmit({ name: values.name.trim(), imo: values.imo.trim() }));
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} onReload={onReload} />
      <div className="grid gap-4 md:grid-cols-2">
        <TextField
          id={`${id}-name`}
          label="Vessel name"
          value={values.name}
          onChange={(value) => set("name", value)}
          error={errors.name}
          hint="Stored in capitals"
          autoCapitalize="characters"
        />
        <TextField
          id={`${id}-imo`}
          label="IMO number"
          inputMode="numeric"
          value={values.imo}
          onChange={(value) => set("imo", value)}
          error={errors.imo}
          hint="7 digits, e.g. 9222118"
        />
      </div>
      <FormActions saving={saving} submitLabel={submitLabel} onCancel={onCancel} />
    </form>
  );
}
